/**
 * Custom Site Specific Javascript
 *
 * This file is to add custom features to the website without having to modify
 * core/common assets. Define a custom class, and build new or overwrite
 * existing methods to create custom functionality.
 */

import { unset } from "lodash";
import { handleProcessClaim, validateField } from "../customSubmitClaim";

/**
 * Validates a form.
 *
 * @param {Object} options - The options object.
 * @param {Event|string} options.event - The event object.
 * @param {CustomSubmitClaim} options.ctx - The current context.
 * @returns {void}
 */
export function validateForm({ event, ctx }) {
    if (event === "init_claim") {
        // Initialize default transaction rows
        onTransactionRowAdded(ctx.purchaseTransactions.container);
        onTransactionRowAdded(ctx.salesTransactions.container);
        organizeContactSection(ctx);
        organizeClaimFieldSection(ctx);
        organizeSignatureSection(ctx);

        // format number inputs
        const jan2020Holdings = ctx.getFormInput("holdings_sept_20_2021");
        const nov2024Holdings = ctx.getFormInput("holdings_aug_18_2022");
        const rightsOfferingShares = ctx.getFormInput("acq_may_21_aug_18");

        jan2020Holdings.addEventListener("blur", formatNumericValue);
        nov2024Holdings.addEventListener("blur", formatNumericValue);
        rightsOfferingShares.addEventListener("blur", formatNumericValue);

        // Move general instructions into claim deadline fieldset
        const claimDeadlineFieldset = document.querySelector(
            "#claimFormDeadline > fieldset"
        );
        const generalInstructions = document.querySelector(
            "#generalInstructionsWrapper"
        );
        claimDeadlineFieldset.appendChild(generalInstructions);

        // Disable email validation for paper claim submissions
        const isPaper = window.location.search.includes("paper_claim=true");
        if (isPaper) disableEmailValidation();
    }

    // only require other account type if selected account type is other
    const accountType = ctx.getFormInput("account_type");
    const otherAccountType = ctx.getFormInput("other_account_type");
    const isOtherAccountType = accountType.value === "other";
    otherAccountType.required = isOtherAccountType;
    otherAccountType.parentElement.classList.toggle(
        "d-none",
        !isOtherAccountType
    );

    // hide sales transactions if claimant does not have any
    const noSalesRecords = ctx.getFormInput("no_sales_records");
    toggleTable(ctx.salesTransactions.container, noSalesRecords.checked);
}

/**
 * Processes a claim for a custom member.
 *
 * @param {CustomSubmitClaim} ctx - The claim to be processed.
 * @returns {Object} The processed claim.
 */
export function customProcessClaim(ctx) {
    const claim = handleProcessClaim.call(ctx, validateForm);

    // Create a new transactions object with only numeric keys from claim.data.transactions
    const transactions = Object.fromEntries(
        Object.entries(claim.data.transactions).filter(([key]) => !isNaN(key))
    );

    const secTransactions = [
        ...processTradeTransactions(transactions),
        createHoldingTransaction({
            type: "B",
            date: "2021-09-20",
            quantity: ctx.getFormInput("holdings_sept_20_2021").value,
        }),
        createHoldingTransaction({
            type: "PZ",
            date: "2022-05-23",
            quantity: ctx.getFormInput("acq_may_21_aug_18").value,
        }),
        createHoldingTransaction({
            type: "U",
            date: "2022-08-18",
            quantity: ctx.getFormInput("holdings_aug_18_2022").value,
        }),
    ];

    return {
        ...claim,
        data: {
            ...claim.data,
            contact: {
                ...claim.data.contact,
                ag_PAPER: window.location.search.includes("paper_claim=true"),
            },
            fields: {
                ...claim.data.fields,
                "Claim Form Type": "Individual",
            },
            transactions,
            securities_transactions: secTransactions,
        },
    };
}

/**
 * Processes trade transactions from the claim data.
 *
 * @param {Object} transactions - The transactions object from the claim data.
 * @returns {Object[]} An array of processed trade transactions.
 */
function processTradeTransactions(transactions) {
    return Object.values(transactions).flatMap((transaction) => {
        const trades = [];

        if (transaction["Date of Purchase/ Acquisition"]) {
            trades.push(
                createTradeTransaction("P", {
                    date: transaction["Date of Purchase/ Acquisition"],
                    quantity:
                        transaction["Number of Shares Purchased/ Acquired"],
                    price: transaction["Purchase/Acquisition Price Per Share"],
                    total: transaction["Total Purchase/Acquisition Price"],
                })
            );
        }

        if (transaction["Date of Sale"]) {
            trades.push(
                createTradeTransaction("S", {
                    date: transaction["Date of Sale"],
                    quantity: transaction["Number of Shares Sold"],
                    price: transaction["Sale Price Per Share"],
                    total: transaction["Total Sale Price"],
                })
            );
        }

        return trades;
    });
}

/**
 * Creates a trade transaction object.
 *
 * @param {string} type - The type of transaction.
 * @param {Object} data - The data object containing date, quantity, price, and total.
 * @param {string} data.date - The date of the transaction.
 * @param {number} data.quantity - The quantity of the transaction.
 * @param {number} data.price - The price of the transaction.
 * @param {number} data.total - The total amount of the transaction.
 * @returns {Object} The created trade transaction object.
 */
function createTradeTransaction(type, { date, quantity, price, total }) {
    return {
        type_of_security: "CS",
        transaction_type: type,
        date_traded: date,
        quantity: quantity,
        price: price,
        total_amt_pd_or_recd: total,
        result_of_short_sale_flag: 0,
    };
}

/**
 * Creates a holding transaction object.
 *
 * @param {Object} data - The data object containing type, date, and quantity.
 * @param {string} data.type - The type of transaction.
 * @param {string} data.date - The date of the transaction.
 * @param {number} data.quantity - The quantity of the transaction.
 * @returns {Object} The created holding transaction object.
 */
function createHoldingTransaction({ type, date, quantity }) {
    return {
        type_of_security: "CS",
        transaction_type: type,
        date_traded: date,
        quantity: quantity,
        price: 0,
        total_amt_pd_or_recd: 0,
        result_of_short_sale_flag: 0,
    };
}

/**
 * Handles the row added event.
 *
 * @param {HTMLElement} row - The row element.
 */
export function onTransactionRowAdded(row) {
    const numericInputs = Array.from(
        row.querySelectorAll('input[type="number"]')
    );
    const dateInputs = Array.from(row.querySelectorAll('input[type="date"]'));
    numericInputs.forEach((input) => {
        input.addEventListener("blur", formatNumericValue);
    });
    dateInputs.forEach((input) => {
        input.addEventListener("change", validateField);
    });
    // move item-id prop from parent of checkbox to checkbox
    const checkbox = row.querySelector('input[type="checkbox"]');
    if (checkbox) {
        const itemId =
            checkbox.parentElement.parentElement.getAttribute("item-id");
        if (itemId) {
            checkbox.setAttribute("item-id", itemId);
        }
    }
}

/**
 * Handles the row removed event.
 *
 * @param {HTMLElement} row - The row element.
 */
export function onTransactionRowRemoved(row) {
    const numericInputs = Array.from(
        row.querySelectorAll('input[type="number"]')
    );
    const dateInputs = Array.from(row.querySelectorAll('input[type="date"]'));
    numericInputs.forEach((input) => {
        input.removeEventListener("blur", formatNumericValue);
    });
    dateInputs.forEach((input) => {
        input.removeEventListener("change", validateField);
    });
}

/**
 * Formats an amount input to 2 decimal places on blur.
 *
 * @param {Event} event - The event object.
 * @returns {void}
 */
function formatNumericValue(event) {
    const input = event.target;
    const value = input.value;

    // If the value is empty or not a valid number, clear it or leave it
    if (value === "" || isNaN(parseFloat(value))) {
        input.value = "";
        return;
    }

    // Format to 2 decimal places
    const numericValue = parseFloat(value);
    // Get the number of decimal places from input.getAttribute('step')
    const decimalPlaces = input.getAttribute("step").split(".")[1].length;
    input.value = numericValue.toFixed(decimalPlaces);
}

/**
 * Organizes the contact section of the form.
 *
 * @param {CustomSubmitClaim} ctx - The current context.
 */
function organizeContactSection() {
    const fieldset = findField("last_name").parentElement;

    // Define field groups and their reference points
    const fieldGroups = [
        {
            fields: [
                "custodian_name",
                "joint_beneficial_owner_name",
                "beneficial_owner_name",
            ],
            referenceField: "last_name",
        },
        {
            fields: [
                "ssn_tin",
                "other_account_type",
                "account_type",
                "account_number",
            ],
            referenceField: "confirm_email_address",
        },
    ];

    // Process each field group
    fieldGroups.forEach(({ fields, referenceField }) => {
        const referenceNode = findField(referenceField);
        moveFields(fields, referenceNode, fieldset);
    });
}

/**
 * Organizes the claim field section of the form.
 *
 * @param {CustomSubmitClaim} ctx - The current context.
 */
function organizeClaimFieldSection(ctx) {
    reCaptionTable(
        ctx.purchaseTransactions.container,
        `<strong>2. PURCHASES/ACQUISITIONS FROM SEPTEMBER 21, 2021 THROUGH MAY 20, 2022</strong> – Separately list each and every
purchase/acquisition of Inotiv common stock (Ticker: NOTV) made from after the opening of trading on September 21, 2021, through
and including the close of trading on May 20, 2022. (Must be documented.)`
    );

    reCaptionTable(ctx.salesTransactions.container, ``);

    relocateTable(ctx.purchaseTransactions.container, "holdings_sept_20_2021");
    relocateTable(ctx.salesTransactions.container, "no_sales_records");
}

function organizeSignatureSection() {
    const lastParagraph = document.querySelector(
        "div.warranties > p:last-child"
    );
    const subjectToBackupWithholding = findField(
        "subject_to_backup_withholding"
    );
    lastParagraph.before(subjectToBackupWithholding, lastParagraph);
}

/**
 * Moves a table below a field in the form.
 *
 * @param {HTMLElement} table - The table to move
 * @param {string} fieldName - The name of the field to move the table below
 */
function relocateTable(table, fieldName) {
    const field = findField(fieldName);
    const tableContainer = table.parentElement;
    const tableActions = table.parentElement.nextSibling;
    tableContainer.classList.remove("mb-3");
    tableActions.classList.add("mb-3");
    field.parentNode.insertBefore(tableActions, field.nextSibling);
    field.parentNode.insertBefore(tableContainer, field.nextSibling);
}

/**
 * Toggle visibility of a table
 *
 * @param {HTMLElement} table - The table to toggle
 * @param {string} isVisible - The desired visibility state
 */
function toggleTable(table, isVisible) {
    const tableContainer = table.parentElement;
    const tableActions = table.parentElement.nextSibling;
    tableContainer.classList.toggle("d-none", isVisible);
    tableActions.classList.toggle("d-none", isVisible);
}

/**
 * Replaces the caption of a table.
 *
 * @param {HTMLElement} table - The table to replace the caption of
 * @param {string} caption - The new caption for the table
 */
function reCaptionTable(table, caption) {
    const captionElement = table.querySelector("caption");
    captionElement.innerHTML = caption;
}

/**
 * Moves a group of fields to a new location in the form.
 *
 * @param {string[]} fieldNames - Array of field names to move
 * @param {Node} referenceNode - The node to insert before
 * @param {HTMLElement} container - The container element
 */
function moveFields(fieldNames, referenceNode, container) {
    fieldNames.forEach((fieldName) => {
        const field = findField(fieldName);
        container.insertBefore(field, referenceNode.nextSibling);
    });
}

/**
 * Finds a field by name.
 *
 * @param {string} fieldName - The name of the field to find
 * @returns {HTMLElement} The field element
 */
function findField(fieldName) {
    const container = document.querySelector(
        `div[data-field-id="_${fieldName}"]`
    );
    if (!container) {
        const input = document.querySelector(
            `input[name="${fieldName}"], textarea[name="${fieldName}"], select[name="${fieldName}"]`
        );
        if (!input) {
            throw new Error(`Field "${fieldName}" not found`);
        }
        return input.type === "checkbox"
            ? input.parentElement.parentElement
            : input.parentElement;
    }
    return container;
}

/**
 * Removes event listeners from number inputs when the form is unloaded.
 *
 * @param {CustomSubmitClaim} ctx - The current context.
 */
export function onFormUnloaded(ctx) {
    // console.log("[Form Unloaded]");
    const jan2020Holdings = ctx.getFormInput("holdings_sept_20_2021");
    const nov2024Holdings = ctx.getFormInput("holdings_aug_18_2022");
    const rightsOfferingShares = ctx.getFormInput("acq_may_21_aug_18");

    jan2020Holdings.removeEventListener("blur", formatNumericValue);
    nov2024Holdings.removeEventListener("blur", formatNumericValue);
    rightsOfferingShares.removeEventListener("blur", formatNumericValue);

    onTransactionRowRemoved(ctx.purchaseTransactions.container);
    onTransactionRowRemoved(ctx.salesTransactions.container);
}

/**
 * Disables email validation logic for email fields
 */
function disableEmailValidation() {
    const emailElements = document.querySelectorAll('[data-validate*="email"]');
    if (emailElements.length === 0) return;
    emailElements.forEach((el) => {
        unset(el.dataset, "validate");
        el.required = undefined;
    });
}
